import { KIT_SUPPORT_EMAIL } from "./kitSubscribe";

/** Content type for Join / Kit confirmation screens. One active item drives both. */
export const CONNECT_SPLASH_TYPE = "connectSplash";

export const DEFAULT_CONFIRMED_AUTO_SECONDS = 7;

export const CONNECT_SPLASH_DEFAULTS = {
  pendingEyebrow: "The Connection Begins",
  pendingTitle: "You're on the list",
  pendingLead:
    "One click left to make sure your place is secured. Open the confirmation email and hit the link.",
  pendingButton: "Back to the music",
  pendingNoteLabel: "Important!",
  pendingNote:
    "Spam and Junk folders can often catch the confirmation email. If you do not see it in your inbox, look in those folders. The sender is Ignite Elysium.",
  pendingSentPrefix: "Sent to",
  showSentEmail: true,
  showPendingNote: true,
  confirmedEyebrow: "Ignite Elysium",
  confirmedTitle: "You're in",
  confirmedLead:
    "Thanks for connecting. We will keep in touch; releases, shows, and updates. No spam.",
  confirmedButton: "Stay with the music",
  confirmedContinue: "Taking you into the site.",
  confirmedAutoSeconds: String(DEFAULT_CONFIRMED_AUTO_SECONDS),
  helpLine: `If you have trouble, email ${KIT_SUPPORT_EMAIL}.`,
  showHelpLine: true,
};

export const CONNECT_SPLASH_FIELD_KEYS = Object.keys(CONNECT_SPLASH_DEFAULTS);

export const CONNECT_SPLASH_TRUE_DEFAULTS = new Set([
  "showSentEmail",
  "showPendingNote",
  "showHelpLine",
]);

export const CONNECT_SPLASH_BOOLEAN_FIELDS = [
  "showSentEmail",
  "showPendingNote",
  "showHelpLine",
];

export const CONNECT_SPLASH_FIELD_META = {
  pendingEyebrow: {
    group: "pending",
    label: "Eyebrow",
    hint: "Small line above the title.",
  },
  pendingTitle: {
    group: "pending",
    label: "Title",
  },
  pendingLead: {
    group: "pending",
    label: "Lead",
    textarea: true,
    hint: "Tell them to open the confirmation email and click the link.",
  },
  pendingButton: {
    group: "pending",
    label: "Button",
  },
  pendingSentPrefix: {
    group: "pending",
    label: "Sent-to prefix",
    hint: "Shown before the address, e.g. Sent to.",
  },
  showSentEmail: {
    group: "pending",
    label: "Show the address we sent to",
  },
  pendingNoteLabel: {
    group: "pending",
    label: "Spam note label",
  },
  pendingNote: {
    group: "pending",
    label: "Spam note",
    textarea: true,
    hint: "Tell them to check spam and junk.",
  },
  showPendingNote: {
    group: "pending",
    label: "Show the spam / junk note",
  },
  confirmedEyebrow: {
    group: "confirmed",
    label: "Eyebrow",
  },
  confirmedTitle: {
    group: "confirmed",
    label: "Title",
  },
  confirmedLead: {
    group: "confirmed",
    label: "Lead",
    textarea: true,
  },
  confirmedButton: {
    group: "confirmed",
    label: "Button",
  },
  confirmedContinue: {
    group: "confirmed",
    label: "Continue line",
    hint: "Quiet line under the lead while the site continues automatically.",
  },
  confirmedAutoSeconds: {
    group: "confirmed",
    label: "Auto-continue seconds",
    hint: "How long the confirmed splash stays up before going into the site. 0 = wait for the button. Blank uses 7.",
  },
  helpLine: {
    group: "shared",
    label: "Help line",
    textarea: true,
    hint: "Quiet line above the button on both screens. Put the support email in the sentence; it becomes a mailto link.",
  },
  showHelpLine: {
    group: "shared",
    label: "Show the help line",
  },
};

export const CONNECT_SPLASH_GROUPS = [
  {
    id: "pending",
    title: "After they join",
    hint: "Shown once Kit accepts the signup; before they confirm by email.",
  },
  {
    id: "confirmed",
    title: "After they confirm",
    hint: "Thank-you splash at /connected (Kit confirmation thank-you URL).",
  },
  {
    id: "shared",
    title: "Both screens",
    hint: "Shown on the pending and confirmed splash, above the button.",
  },
];

const asFlag = (value, fallback) => {
  if (value == null || value === "") return fallback;
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  const v = String(value).trim().toLowerCase();
  if (v === "true" || v === "1" || v === "yes") return true;
  if (v === "false" || v === "0" || v === "no") return false;
  return fallback;
};

const asText = (value, fallback) => {
  if (value == null) return fallback;
  const s = String(value).trim();
  return s === "" ? fallback : s;
};

export const parseConfirmedAutoSeconds = (value) => {
  if (value == null || String(value).trim() === "") {
    return DEFAULT_CONFIRMED_AUTO_SECONDS;
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return DEFAULT_CONFIRMED_AUTO_SECONDS;
  return Math.min(120, n);
};

export const isConnectSplashType = (type) =>
  String(type || "")
    .trim()
    .toLowerCase() === CONNECT_SPLASH_TYPE.toLowerCase();

/** Stored on the existing `text` field so copy survives a strict content schema. */
export const CONNECT_SPLASH_PACK_PREFIX = "ms1-connect-splash:";

export const packConnectSplashIntoItem = (item) => {
  if (!item || typeof item !== "object") return item;
  if (!isConnectSplashType(item.type)) return item;
  const packed = {};
  CONNECT_SPLASH_FIELD_KEYS.forEach((key) => {
    packed[key] = item[key] == null ? "" : item[key];
  });
  return {
    ...item,
    text: CONNECT_SPLASH_PACK_PREFIX + JSON.stringify(packed),
  };
};

export const unpackConnectSplashFromItem = (item) => {
  if (!item || typeof item !== "object") return item;
  if (!isConnectSplashType(item.type)) return item;
  const raw = item.text;
  if (typeof raw !== "string" || !raw.startsWith(CONNECT_SPLASH_PACK_PREFIX)) {
    return item;
  }
  try {
    const packed = JSON.parse(raw.slice(CONNECT_SPLASH_PACK_PREFIX.length));
    if (!packed || typeof packed !== "object" || Array.isArray(packed)) {
      return item;
    }
    const next = { ...item };
    // Packed `text` is the source of truth. Extra keys can stick on the
    // document after create and then be ignored by a strict schema on
    // update; preferring those leftovers would freeze the first save.
    CONNECT_SPLASH_FIELD_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(packed, key)) {
        next[key] = packed[key];
      }
    });
    return next;
  } catch {
    return item;
  }
};

export const findConnectSplashItem = (content) => {
  if (!content || typeof content !== "object") return null;
  const items = Object.values(content).filter((item) =>
    isConnectSplashType(item?.type),
  );
  if (!items.length) return null;
  const active = items.find((item) => asFlag(item.active, false));
  return active ? unpackConnectSplashFromItem(active) : null;
};

/** Merge an optional CMS item onto hardcoded defaults. Blank fields keep defaults. */
export const resolveConnectSplash = (content) => {
  const item = findConnectSplashItem(content) || {};
  const d = CONNECT_SPLASH_DEFAULTS;
  return {
    pending: {
      eyebrow: asText(item.pendingEyebrow, d.pendingEyebrow),
      title: asText(item.pendingTitle, d.pendingTitle),
      lead: asText(item.pendingLead, d.pendingLead),
      button: asText(item.pendingButton, d.pendingButton),
      noteLabel: asText(item.pendingNoteLabel, d.pendingNoteLabel),
      note: asText(item.pendingNote, d.pendingNote),
      sentPrefix: asText(item.pendingSentPrefix, d.pendingSentPrefix),
      showSentEmail: asFlag(item.showSentEmail, d.showSentEmail),
      showNote: asFlag(item.showPendingNote, d.showPendingNote),
    },
    confirmed: {
      eyebrow: asText(item.confirmedEyebrow, d.confirmedEyebrow),
      title: asText(item.confirmedTitle, d.confirmedTitle),
      lead: asText(item.confirmedLead, d.confirmedLead),
      button: asText(item.confirmedButton, d.confirmedButton),
      continueLine: asText(item.confirmedContinue, d.confirmedContinue),
      autoSeconds: parseConfirmedAutoSeconds(item.confirmedAutoSeconds),
    },
    helpLine: asText(item.helpLine, d.helpLine),
    showHelpLine: asFlag(item.showHelpLine, d.showHelpLine),
  };
};
